// frontend/modules/notification-config/components/NotificationConfigImportExport.tsx
import React, { useRef, useState } from 'react';
import { Button, Modal, Space, Typography, Alert, Progress, List } from 'antd';
import { UploadOutlined, DownloadOutlined, QuestionCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';

const { Title, Text, Paragraph } = Typography;

interface NotificationConfigImportExportProps {
    onExport: () => void;
    onImport: (file: File) => Promise<void>;
    isLoading: boolean;
    importResult?: {
        importedCount: number;
        skippedCount: number;
        errorCount: number;
        errors: string[];
        warnings: string[];
        totalProcessed: number;
    };
}

const NotificationConfigImportExport: React.FC<NotificationConfigImportExportProps> = ({
    onExport,
    onImport,
    isLoading,
    importResult
}) => {
    const { dark } = useTheme();
    const [isImportModalVisible, setIsImportModalVisible] = useState(false);
    const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
    const [isResultModalVisible, setIsResultModalVisible] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportClick = () => {
        setIsImportModalVisible(true);
    };

    const handleImportConfirm = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsImportModalVisible(false);
            await onImport(file);
            
            // Show results modal after import
            setIsResultModalVisible(true);

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const showHelpModal = () => {
        setIsHelpModalVisible(true);
    };

    const getImportStatusType = () => {
        if (!importResult) return 'info';
        if (importResult.errorCount > 0) return 'error';
        if (importResult.skippedCount > 0) return 'warning';
        return 'success';
    };

    const getImportStatusText = () => {
        if (!importResult) return '';
        const { importedCount, skippedCount, errorCount, totalProcessed } = importResult;
        return `Processed ${totalProcessed} configurations: ${importedCount} imported, ${skippedCount} skipped, ${errorCount} errors`;
    };

    return (
        <div>
            <Space>
                <Button
                    onClick={onExport}
                    icon={<DownloadOutlined />}
                    loading={isLoading}
                >
                    Export Configurations
                </Button>
                <Button
                    onClick={handleImportClick}
                    icon={<UploadOutlined />}
                    loading={isLoading}
                >
                    Import Configurations
                </Button>
                <Button
                    type="link"
                    onClick={showHelpModal}
                    icon={<QuestionCircleOutlined />}
                >
                    Help
                </Button>
            </Space>

            {/* Hidden file input for import */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".json"
                onChange={handleFileChange}
            />

            {/* Import Confirmation Modal */}
            <Modal
                title="Import Notification Configurations"
                open={isImportModalVisible}
                onOk={handleImportConfirm}
                onCancel={() => setIsImportModalVisible(false)}
                okText="Select File"
                className={dark ? 'dark' : ''}
            >
                <Alert
                    message="Warning"
                    description="Importing configurations may overwrite existing configurations with the same code. Make sure you have exported your existing configurations as a backup."
                    type="warning"
                    showIcon
                    className="mb-4"
                />
                <Paragraph>
                    Select a JSON file containing notification configurations to import. The file should be in the same format as the exported configurations.
                </Paragraph>
            </Modal>

            {/* Import Results Modal */}
            <Modal
                title="Import Results"
                open={isResultModalVisible}
                onOk={() => setIsResultModalVisible(false)}
                onCancel={() => setIsResultModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsResultModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={600}
                className={dark ? 'dark' : ''}
            >
                {importResult && (
                    <div className="space-y-4">
                        <Alert
                            message="Import Complete"
                            description={getImportStatusText()}
                            type={getImportStatusType()}
                            showIcon
                        />

                        {importResult.importedCount > 0 && (
                            <div>
                                <Text strong className="text-green-600">
                                    Successfully Imported: {importResult.importedCount}
                                </Text>
                            </div>
                        )}

                        {importResult.skippedCount > 0 && (
                            <div>
                                <Text strong className="text-yellow-600">
                                    Skipped: {importResult.skippedCount}
                                </Text>
                            </div>
                        )}

                        {importResult.errorCount > 0 && (
                            <div>
                                <Text strong className="text-red-600">
                                    Errors: {importResult.errorCount}
                                </Text>
                            </div>
                        )}

                        {importResult.warnings.length > 0 && (
                            <div>
                                <Title level={5}>Warnings:</Title>
                                <List
                                    size="small"
                                    dataSource={importResult.warnings}
                                    renderItem={(warning) => (
                                        <List.Item>
                                            <Text type="warning">{warning}</Text>
                                        </List.Item>
                                    )}
                                />
                            </div>
                        )}

                        {importResult.errors.length > 0 && (
                            <div>
                                <Title level={5}>Errors:</Title>
                                <List
                                    size="small"
                                    dataSource={importResult.errors}
                                    renderItem={(error) => (
                                        <List.Item>
                                            <Text type="danger">{error}</Text>
                                        </List.Item>
                                    )}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Help Modal */}
            <Modal
                title="Import/Export Help"
                open={isHelpModalVisible}
                onOk={() => setIsHelpModalVisible(false)}
                onCancel={() => setIsHelpModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsHelpModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={600}
                className={dark ? 'dark' : ''}
            >
                <div className="space-y-4">
                    <div>
                        <Title level={4}>
                            <FileTextOutlined className="mr-2" />
                            Export Configurations
                        </Title>
                        <Paragraph>
                            Export all notification configurations to a JSON file. This creates a backup of your current configurations
                            that can be imported later or shared with other environments.
                        </Paragraph>
                    </div>

                    <div>
                        <Title level={4}>
                            <UploadOutlined className="mr-2" />
                            Import Configurations
                        </Title>
                        <Paragraph>
                            Import notification configurations from a JSON file. The file must contain an array of notification
                            configuration objects in the correct format.
                        </Paragraph>
                        <Alert
                            message="Important Notes"
                            description={
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Configurations with existing codes will be skipped by default</li>
                                    <li>Use the "overwrite existing" option to update existing configurations</li>
                                    <li>Invalid configurations will be skipped and reported in the results</li>
                                    <li>Always backup your existing configurations before importing</li>
                                </ul>
                            }
                            type="info"
                            showIcon
                        />
                    </div>

                    <div>
                        <Title level={4}>File Format</Title>
                        <Paragraph>
                            The JSON file should contain an array of notification configuration objects with the following structure:
                        </Paragraph>
                        <pre className={`p-3 rounded text-sm ${dark ? 'bg-gray-800 text-white' : 'bg-gray-100'}`}>
{`[
  {
    "code": "LOAN_APPROVAL_REQUEST",
    "name": "Loan Approval Request",
    "description": "Notification sent when loan approval is required",
    "platform": "EMAIL",
    "templateName": "loan-approval-template",
    "contextScriptId": "uuid-of-context-script",
    "active": true,
    "contactGroups": "{\\"groupKeys\\": [\\"APPROVERS\\", \\"MANAGERS\\"]}"
  }
]`}
                        </pre>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default NotificationConfigImportExport;
